import { TMasterLanguageTest } from "@/api/entities/master-language-test.entity";
import { TWorkExperience } from "@/api/entities/talent.entity";
import { TCreateTalentReqDto } from "@/api/services/main/talent/dtos/create-talent-req.dto";
import { TGetTalentDto } from "@/api/services/main/talent/dtos/get-talent.dto";
import { TUpdateTalentReqDto } from "@/api/services/main/talent/dtos/update-talent-req.dto";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { TTalentForm, TTalentUploadFile } from "@/features/talent/types/talentForm";
import { dayjs } from "@/utils/dayjs";
import { normalizeString } from "@/utils/string";

const cleanArray = <T>(arr: (T | null | undefined)[] | null | undefined): T[] | null => {
  if (!arr) return null;

  const cleaned = arr.filter((item): item is T => {
    if (item == null) return false;
    if (typeof item === "string") return item.trim() !== "";
    if (typeof item === "object") return Object.keys(item as object).some((k) => (item as Record<string, unknown>)[k] != null);
    return true;
  });

  return cleaned.length > 0 ? cleaned : null;
};

const isWorkExperienceEmpty = (exp: TWorkExperience): boolean => {
  return (
    (!exp.jobType || exp.jobType === "") &&
    (!exp.companyName || exp.companyName === "") &&
    (!exp.startDate || exp.startDate === null) &&
    (!exp.endDate || exp.endDate === null)
  );
};

export const convertTalentFormDataReq = (data: TTalentForm, languageTestData: TMasterLanguageTest[]): TCreateTalentReqDto | TUpdateTalentReqDto => {
  const cleanedWorkExperiences = cleanArray(
    data.workExperiences
      ?.filter((exp) => !isWorkExperienceEmpty(exp))
      .map((exp) => ({
        id: exp.id ?? undefined,
        jobType: normalizeString(exp.jobType),
        companyName: normalizeString(exp.companyName),
        startDate: exp.startDate ? dayjs(exp.startDate).format(DATE_FORMAT_FOR_API) : null,
        endDate: exp.endDate ? dayjs(exp.endDate).format(DATE_FORMAT_FOR_API) : null,
      })),
  );

  const cleanedLanguageTestLevelIds = cleanArray(data?.languageTestLevelIds);

  const languageCertificates: Array<{ languageTestId: number; languageTestLevelId?: number | null }> = [];

  if (cleanedLanguageTestLevelIds && cleanedLanguageTestLevelIds.length > 0) {
    cleanedLanguageTestLevelIds.forEach((id) => {
      if (id === null || id === undefined) return;

      if (id < 0) {
        // Negative value represents test without levels
        const testId = -id;
        languageCertificates.push({
          languageTestId: testId,
          languageTestLevelId: null,
        });
      } else {
        // Positive value represents level id
        const test = languageTestData.find((t) => t.levels.some((level) => level.id.toString() === id.toString()));
        if (test) {
          languageCertificates.push({
            languageTestId: test.id,
            languageTestLevelId: id,
          });
        }
      }
    });
  }

  const raw = {
    recruitmentStatus: data.recruitmentStatus,
    isRecommendedTalent: data.isRecommendedTalent ?? false,
    fullName: normalizeString(data.fullName),
    birthday: data.birthday ? dayjs(data.birthday).format(DATE_FORMAT_FOR_API) : null,
    nickName: normalizeString(data.nickName),
    talentNumber: normalizeString(data.talentNumber),
    nationalityId: data.nationalityId ?? undefined,
    gender: data.gender ?? undefined,
    phoneNumber: data.phoneNumber ? (normalizeString(data.phoneNumber)?.replaceAll("-", "") ?? null) : null,
    email: normalizeString(data.email),
    location: data.location ?? undefined,
    religion: normalizeString(data.religion),
    zipCode: data.zipCode ?? undefined,
    prefecture: normalizeString(data.prefecture),
    city: normalizeString(data.city),
    address: normalizeString(data.address),
    country: normalizeString(data.country),
    avatarFile: data.avatarFile ?? null,
    avatarFilePath: data.avatarFilePath ?? null,
    passportNumber: normalizeString(data.passportNumber),
    passportExpiryDate: data.passportExpiryDate ? dayjs(data.passportExpiryDate).format(DATE_FORMAT_FOR_API) : null,
    residenceStatusId: data.residenceStatusId,
    periodOfStay: data.periodOfStay ? dayjs(data.periodOfStay).format(DATE_FORMAT_FOR_API) : null,
    specifiedSkillFirstStartDate: data.specifiedSkillFirstStartDate ? dayjs(data.specifiedSkillFirstStartDate).format(DATE_FORMAT_FOR_API) : null,
    blankPeriodMonthly: normalizeString(data.blankPeriodMonthly),
    specifiedSkillFirstRemainingTerm: normalizeString(data.specifiedSkillFirstRemainingTerm),
    languageCertificates: languageCertificates.length > 0 ? languageCertificates : null,
    languageTestOther: normalizeString(data.languageTestOther),
    companySpecificIndustryId: data.companySpecificIndustryId ?? null,
    specificBusinessClassificationIds: data.specificBusinessClassificationIds ?? null,
    specificSkillCertificates: cleanArray(data.specificSkillCertificates),
    languageSkills: cleanArray(
      data.languageSkills?.map((skill) => ({
        ...skill,
        content: normalizeString(skill?.content),
      })),
    ),
    workExperiences: cleanedWorkExperiences,
    educationBackground: cleanArray(data.educationBackground),
    heldQualifications: cleanArray(data.heldQualifications?.map((qual) => normalizeString(qual))),
    memo: normalizeString(data.memo),
    interviewEvaluation: normalizeString(data.interviewEvaluation),
    familyInformation: normalizeString(data.familyInformation),
    majorId: data.majorId ?? null,
    jobTypeId: data.jobTypeId ?? null,
    desiredJobTypeIds: data.desiredJobTypeIds ?? null,
    desiredWorkLocation: normalizeString(data.desiredWorkLocation),
    expectSalary: normalizeString(data.expectSalary),
    availableFrom: normalizeString(data.availableFrom),
    otherRequest: normalizeString(data.otherRequest),
    internalMemo: normalizeString(data.internalMemo),
    interviewDate: data.interviewDate ? dayjs(data.interviewDate).format(DATE_FORMAT_FOR_API) : null,
    interviewerId: data.interviewerId ?? null,
    partnerId: data.partnerId ?? null,
    personInChargeId: data.personInChargeId,
    relatedFiles: data.relatedFiles ?? null,
    relatedFilePaths: JSON.stringify(data.relatedFilePaths) ?? null,
    relatedFileTypes: JSON.stringify(data.relatedFileTypes) ?? null,
  };

  const payload = raw as TCreateTalentReqDto;
  return payload;
};

export const convertResToFormData = (data: TGetTalentDto): Partial<TTalentForm> | undefined => {
  if (!data) return undefined;

  const specificSkillCertificatesStr = data?.specificSkillCertificates
    ?.map((item) => item?.specificSkillCertificate?.certificateName)
    .filter(Boolean)
    .join("、");

  const languageTestLevelIdsStr = data?.languageCertificates
    ?.map((cert) => {
      if (cert?.languageTestLevel?.level) {
        return cert.languageTestLevel.level;
      }
      return null;
    })
    .filter(Boolean)
    .join("、");

  let specifiedSkillFirstRemainingTermYear: string | undefined;
  let specifiedSkillFirstRemainingTermMonth: string | undefined;

  if (data?.specifiedSkillFirstRemainingTerm) {
    [specifiedSkillFirstRemainingTermYear, specifiedSkillFirstRemainingTermMonth] = data.specifiedSkillFirstRemainingTerm.split("/");
  }

  const filteredWorkExperiences = data?.workExperiences?.filter((exp) => !isWorkExperienceEmpty(exp)) || [];
  const parsedWorkExperiences =
    filteredWorkExperiences.length > 0
      ? filteredWorkExperiences.map((exp) => ({
          ...exp,
          startDate: exp.startDate ? dayjs(exp.startDate) : null,
          endDate: exp.endDate ? dayjs(exp.endDate) : null,
        }))
      : undefined;

  const dataTestLevels: (number | null)[] = [];
  if (data?.languageCertificates && data.languageCertificates.length > 0) {
    data.languageCertificates.forEach((cert) => {
      if (cert?.languageTestLevel?.id) {
        dataTestLevels.push(cert.languageTestLevel.id);
      } else if (cert?.languageTestId) {
        dataTestLevels.push(-cert.languageTestId);
      }
    });
  }
  const dataTestLevelIds = dataTestLevels.length > 0 ? dataTestLevels : null;

  let talentUploadFiles: TTalentUploadFile[] = [];
  if (data?.talentUploadFiles?.length) {
    talentUploadFiles = [...data?.talentUploadFiles].sort((a, b) => Number(a.id) - Number(b.id));
  }

  const response = {
    recruitmentStatus: data.recruitmentStatus,
    isRecommendedTalent: data.isRecommendedTalent,
    fullName: data.fullName,
    birthday: data.birthday ? dayjs(data.birthday) : null,
    nickName: data.nickName,
    talentNumber: data.talentNumber,
    nationalityId: data?.nationality ? Number(data?.nationality?.id) : undefined,
    nationality: data.nationality,
    specificBusinessClassifications: data.specificBusinessClassifications ?? [],
    specificBusinessClassificationIds: data.specificBusinessClassifications?.map((item) => item.id) ?? null,
    companySpecificIndustry: data.companySpecificIndustry,
    companySpecificIndustryId: data.companySpecificIndustryId ?? data.companySpecificIndustry?.id ?? null,
    languageCertificates: data.languageCertificates ?? [],
    gender: data.gender,
    phoneNumber: data.phoneNumber,
    email: data.email,
    religion: data.religion,
    zipCode: data.zipCode ?? undefined,
    prefecture: data.prefecture,
    city: data.city,
    address: data.address,
    country: data.country,
    avatarFilePath: data.avatarFilePath,
    passportNumber: data.passportNumber,
    passportExpiryDate: data.passportExpiryDate ? dayjs(data.passportExpiryDate) : null,
    residenceStatusId: data?.residenceStatus?.id,
    residenceStatus: data.residenceStatus,
    periodOfStay: data.periodOfStay ? dayjs(data.periodOfStay) : null,
    specifiedSkillFirstStartDate: data.specifiedSkillFirstStartDate ? dayjs(data.specifiedSkillFirstStartDate) : null,
    specifiedSkillFirstRemainingTerm: data.specifiedSkillFirstRemainingTerm,
    languageTestLevelIds: dataTestLevelIds,
    languageTestLevelIdsStr: languageTestLevelIdsStr ?? null,
    languageTestOther: data.languageTestOther,
    jobTypeId: data?.jobType?.id,
    specificSkillCertificates: data?.specificSkillCertificates?.map((item) => item?.specificSkillCertificate?.id),
    specificSkillCertificateStr: specificSkillCertificatesStr,
    languageSkills: data.languageSkills ?? null,
    workExperiences: parsedWorkExperiences,
    educationBackground: data.educationBackground,
    heldQualifications: data.heldQualifications,
    memo: data.memo,
    interviewEvaluation: data.interviewEvaluation,
    familyInformation: data.familyInformation,
    majorId: data?.major?.id,
    major: data.major,
    desiredJobTypeIds: data?.desiredJobTypes?.map((item) => Number(item.id)) ?? null,
    desiredJobTypes: data.desiredJobTypes,
    jobType: data.jobType,
    location: data.location,
    desiredWorkLocation: data.desiredWorkLocation,
    expectSalary: data.expectSalary,
    blankPeriodMonthly: data.blankPeriodMonthly,
    availableFrom: data.availableFrom,
    otherRequest: data.otherRequest,
    internalMemo: data.internalMemo,
    interviewDate: data.interviewDate ? dayjs(data.interviewDate) : null,
    interviewer: data.interviewer,
    interviewerId: data.interviewerId,
    partner: data.partner,
    partnerId: data.partner?.id,
    personInCharge: data.personInCharge,
    personInChargeId: data.personInCharge?.id,
    talentUploadFiles: talentUploadFiles,
    specifiedSkillFirstRemainingTermMonth: specifiedSkillFirstRemainingTermMonth ? Number(specifiedSkillFirstRemainingTermMonth) : undefined,
    specifiedSkillFirstRemainingTermYear: specifiedSkillFirstRemainingTermYear ? Number(specifiedSkillFirstRemainingTermYear) : undefined,
    caseMatches: data.caseMatches,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };

  return response;
};

const normalizeEmptyValue = (value: unknown): unknown => {
  if (value === null || value === "" || value === undefined) {
    return null;
  }
  return value;
};

export const isValueDifferent = (current: unknown, initial: unknown): boolean => {
  const normalizedCurrent = normalizeEmptyValue(current);
  const normalizedInitial = normalizeEmptyValue(initial);

  if (normalizedCurrent === normalizedInitial) return false;
  if (normalizedCurrent == null && normalizedInitial == null) return false;
  if (normalizedCurrent == null || normalizedInitial == null) return true;
  if (typeof normalizedCurrent !== typeof normalizedInitial) return true;
  if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedInitial)) {
    if (normalizedCurrent.length !== normalizedInitial.length) return true;
    for (let i = 0; i < normalizedCurrent.length; i++) {
      if (isValueDifferent(normalizedCurrent[i], normalizedInitial[i])) return true;
    }
    return false;
  }
  if (typeof normalizedCurrent === "object" && typeof normalizedInitial === "object") {
    const currentKeys = Object.keys(normalizedCurrent as Record<string, unknown>);
    const initialKeys = Object.keys(normalizedInitial as Record<string, unknown>);
    if (currentKeys.length !== initialKeys.length) return true;
    for (const key of currentKeys) {
      if (!initialKeys.includes(key)) return true;
      if (isValueDifferent((normalizedCurrent as Record<string, unknown>)[key], (normalizedInitial as Record<string, unknown>)[key])) return true;
    }
    return false;
  }
  return normalizedCurrent !== normalizedInitial;
};

export const isDefaultValuesChanged = (prev: Partial<TTalentForm> | undefined, current: Partial<TTalentForm> | undefined): boolean => {
  if (prev === current) return false;
  if (prev == null && current == null) return false;
  if (prev == null || current == null) return true;
  return isValueDifferent(prev, current);
};

const isEmptyArrayValue = (value: unknown[]): boolean => {
  if (value.length === 0) return true;
  return value.every((item) => {
    if (item === null || item === undefined) return true;
    if (typeof item === "string" && item.trim() === "") return true;
    if (typeof item === "object" && !Array.isArray(item)) {
      const keys = Object.keys(item);
      if (keys.length === 0) return true;
      return keys.every((key) => {
        const objValue = (item as Record<string, unknown>)[key];
        return isEmptyValue(objValue);
      });
    }
    return false;
  });
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value === "boolean") return false;
  if (typeof value === "number") {
    return value === 0;
  }

  if (typeof value === "object" && value !== null && "isValid" in value && typeof (value as { isValid?: () => boolean }).isValid === "function") {
    const dayjsObj = value as { isValid?: () => boolean };
    return !dayjsObj.isValid || !dayjsObj.isValid();
  }
  if (Array.isArray(value)) {
    return isEmptyArrayValue(value);
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    return keys.every((key) => {
      const objValue = (value as Record<string, unknown>)[key];
      return isEmptyValue(objValue);
    });
  }
  return false;
};

export const isFormDataEmpty = (data: Partial<TTalentForm>): boolean => {
  const fieldsToCheck = Object.entries(data).filter(([key]) => {
    return !["recruitmentStatus", "id", "isRecommendedTalent"].includes(key);
  });

  if (fieldsToCheck.length === 0) return true;

  return fieldsToCheck.every(([_, value]) => isEmptyValue(value));
};
